import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  Building2,
  CheckSquare,
  Target,
  Users,
} from 'lucide-react';
import Toast from '../../components/Toast';
import { useToast } from '../../hooks/useToast';
import apiService from '../../services/api';
import {
  formatNr2jDate,
  NR2J_ACTIVE_PIPELINE_STAGES,
  nr2jStageLabel,
  organizationDisplayName,
} from '../../utils/nr2jConstants';
import {
  approvalDocumentLabel,
  approvalUserName,
  nr2jApprovalTypeLabel,
  summarizeApprovals,
} from '../../utils/nr2jApprovals';
import { readAdminSessionUser } from '../../utils/settingsSessionUser.js';

function KpiCard({ title, value, to, icon: Icon, tone = 'slate' }) {
  const tones = {
    slate: 'border-slate-200 bg-white',
    amber: 'border-amber-200 bg-amber-50',
    rose: 'border-rose-200 bg-rose-50',
    emerald: 'border-emerald-200 bg-emerald-50',
  };
  return (
    <Link
      to={to}
      className={`rounded-xl border p-4 shadow-sm hover:shadow-md transition ${tones[tone] || tones.slate}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm text-gray-600">{title}</p>
          <p className="mt-1 text-3xl font-semibold text-gray-900">{value ?? '—'}</p>
        </div>
        {Icon ? <Icon className="h-6 w-6 text-gray-500" /> : null}
      </div>
      <p className="mt-3 text-xs font-medium text-indigo-700 inline-flex items-center gap-1">
        Voir la liste <ArrowRight className="h-3.5 w-3.5" />
      </p>
    </Link>
  );
}

export default function Nr2jDashboard() {
  const navigate = useNavigate();
  const { toasts, toast, removeToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [approvals, setApprovals] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.getNr2jDashboard();
      setData(res);
      try {
        const queue = await apiService.listNr2jApprovals();
        setApprovals(queue?.approvals || []);
      } catch {
        setApprovals([]);
      }
    } catch (error) {
      toast.error(error?.message || 'Impossible de charger le tableau de bord NR2J');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const approvalSummary = summarizeApprovals(approvals);
  const kpis = data?.kpis || {};
  const byStage = (data?.byStage || []).filter((row) =>
    NR2J_ACTIVE_PIPELINE_STAGES.includes(row.stage),
  );
  const upcomingTasks = data?.upcomingTasks || [];
  const recentActivity = data?.recentActivity || [];
  const session = readAdminSessionUser();
  const isManager = session?.platformProfile === 'MANAGER_NR2J' || session?.role === 'SUPER_ADMIN';
  const scopeLabel = session?.role === 'SUPER_ADMIN'
    ? 'Périmètre : tous les dossiers'
    : session?.platformProfile === 'MANAGER_NR2J'
      ? 'Périmètre : mes dossiers + mon équipe (reports directs)'
      : session?.platformProfile === 'ADMINISTRATIF_NR2J'
        ? 'Périmètre : tous les dossiers'
        : 'Périmètre : mes dossiers';

  return (
    <div className="space-y-6" data-testid="nr2j-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion NR2J</h1>
          <p className="text-sm text-gray-600">{scopeLabel}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isManager ? (
            <button
              type="button"
              onClick={() => navigate('/nr2j/mon-equipe')}
              className="inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-white px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50"
            >
              <Users className="h-4 w-4" />
              {session?.role === 'SUPER_ADMIN' ? 'Suivi commercial' : 'Mon équipe'}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => navigate('/prospection')}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            <Target className="h-4 w-4" />
            Ouvrir la prospection
          </button>
        </div>
      </div>

      {loading ? (
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-500">
          Chargement…
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <KpiCard
              title="Prospects à relancer"
              value={kpis.prospectsToFollowUp ?? 0}
              to="/prospection?filter=followup"
              icon={AlertTriangle}
              tone="amber"
            />
            <KpiCard
              title="Prospects en cours"
              value={kpis.prospectsOpen ?? 0}
              to="/prospection"
              icon={Users}
              tone="slate"
            />
            <KpiCard
              title="Clients NR2J"
              value={kpis.clients ?? 0}
              to="/nr2j/clients"
              icon={Building2}
              tone="emerald"
            />
            <KpiCard
              title="Tâches en retard"
              value={kpis.tasksOverdue ?? 0}
              to="/nr2j/taches?filter=overdue"
              icon={CheckSquare}
              tone="rose"
            />
          </div>

          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm" data-testid="nr2j-validation-queue">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">À valider</h2>
              {isManager ? (
                <Link
                  to="/nr2j/a-valider"
                  className="text-xs font-medium text-indigo-700"
                  data-testid="nr2j-dashboard-approvals-link"
                >
                  Tout voir ({approvalSummary.total})
                </Link>
              ) : null}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Devis {approvalSummary.quotes}
              {' · '}Factures {approvalSummary.invoices}
              {' · '}Avoirs {approvalSummary.creditNotes}
            </p>
            <ul className="mt-4 space-y-2">
              {approvals.length === 0 ? (
                <li className="text-sm text-gray-500">Aucune demande en attente.</li>
              ) : approvals.slice(0, 5).map((row) => (
                <li key={row.id}>
                  <Link
                    to={isManager ? '/nr2j/a-valider' : (row.document?.href || '/nr2j')}
                    className="flex items-center justify-between gap-3 rounded-lg px-3 py-2 hover:bg-gray-50"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-gray-900">
                        {nr2jApprovalTypeLabel(row.entityType)} · {approvalDocumentLabel(row)}
                      </p>
                      <p className="truncate text-xs text-gray-500">
                        {approvalUserName(row.requestedBy)}
                        {row.document?.organizationName ? ` · ${row.document.organizationName}` : ''}
                        {' · '}{formatNr2jDate(row.requestedAt)}
                      </p>
                    </div>
                    <ArrowRight className="h-4 w-4 flex-shrink-0 text-gray-400" />
                  </Link>
                </li>
              ))}
            </ul>
          </section>

          <div className="grid gap-6 lg:grid-cols-2">
            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                Suivi commercial
              </h2>
              <p className="mt-1 text-xs text-gray-500">
                Pipeline actif uniquement — les clients convertis sont dans Clients NR2J.
              </p>
              <ul className="mt-4 space-y-2">
                {byStage.length === 0 ? (
                  <li className="text-sm text-gray-500">Aucune société pour le moment.</li>
                ) : (
                  byStage.map((row) => (
                    <li key={row.stage}>
                      <Link
                        to={`/prospection?stage=${encodeURIComponent(row.stage)}`}
                        className="flex items-center justify-between rounded-lg px-3 py-2 hover:bg-gray-50"
                      >
                        <span className="text-sm text-gray-800">{nr2jStageLabel(row.stage)}</span>
                        <span className="text-sm font-semibold text-gray-900">{row.count}</span>
                      </Link>
                    </li>
                  ))
                )}
              </ul>
              <Link
                to="/prospection?filter=PERDU"
                className="mt-3 inline-block text-xs font-medium text-gray-500 hover:text-indigo-700"
                data-testid="nr2j-dashboard-lost-link"
              >
                Voir les prospects perdus
              </Link>
            </section>

            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                  Tâches à venir
                </h2>
                <Link to="/nr2j/taches" className="text-xs font-medium text-indigo-700">
                  Toutes
                </Link>
              </div>
              <ul className="mt-4 space-y-2">
                {upcomingTasks.length === 0 ? (
                  <li className="text-sm text-gray-500">Aucune tâche planifiée.</li>
                ) : (
                  upcomingTasks.map((task) => (
                    <li key={task.id}>
                      <button
                        type="button"
                        onClick={() => navigate(`/nr2j/clients/${task.organizationId}`)}
                        className="w-full rounded-lg px-3 py-2 text-left hover:bg-gray-50"
                      >
                        <p className="text-sm font-medium text-gray-900">{task.title}</p>
                        <p className="text-xs text-gray-500">
                          {organizationDisplayName(task.organization)} · {formatNr2jDate(task.dueAt)}
                        </p>
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </section>
          </div>

          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
              Activité récente
            </h2>
            <ul className="mt-4 divide-y divide-gray-100">
              {recentActivity.length === 0 ? (
                <li className="py-3 text-sm text-gray-500">Aucun événement.</li>
              ) : (
                recentActivity.map((ev) => (
                  <li key={ev.id} className="py-3">
                    <button
                      type="button"
                      onClick={() => navigate(`/nr2j/clients/${ev.organizationId}`)}
                      className="w-full text-left hover:opacity-80"
                    >
                      <p className="text-sm text-gray-900">{ev.message}</p>
                      <p className="text-xs text-gray-500">{formatNr2jDate(ev.createdAt)}</p>
                    </button>
                  </li>
                ))
              )}
            </ul>
          </section>
        </>
      )}

      {toasts.map((item) => (
        <Toast key={item.id} toast={item} onClose={() => removeToast(item.id)} />
      ))}
    </div>
  );
}
